'use client';
import * as styles from './styles.css';
export { styles as tabsStyles };
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useState, useLayoutEffect } from 'react';
import { cx, throttle, hasNavigator, KeyboardCode } from '@paypalcorp/pp-react-utils';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { ArrowLeftIcon, ArrowRightIcon } from '@paypalcorp/pp-react-icons';

var _excluded$2 = ["id", "tabIdx", "label", "tabGroupId", "panelId", "selected", "onClick", "className"];
var Tab = React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
    tabIdx = _ref.tabIdx,
    label = _ref.label,
    tabGroupId = _ref.tabGroupId,
    panelId = _ref.panelId,
    selected = _ref.selected,
    onClick = _ref.onClick,
    className = _ref.className,
    rest = _objectWithoutProperties(_ref, _excluded$2);
  var tabsStyle = cx(styles.tabsBtn, selected && styles.tabsSelectedBtn, className);
  var focusable = selected ? 0 : -1;
  var tabId = id || "".concat(tabGroupId, "-tab").concat(parseInt(tabIdx, 10) + 1);
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref,
    "data-tab-index": tabIdx,
    id: tabId,
    className: tabsStyle,
    "aria-controls": panelId,
    "aria-selected": selected,
    tabIndex: focusable,
    type: "button",
    role: "tab"
  }, rest, {
    onClick: onClick,
    "data-button-text": label,
    "data-ppui": "true"
  }), label);
});

var _excluded$1 = ["id", "panelId", "label"];
var isRunningOnIEorEdge = function () {
  if (hasNavigator() && (/MSIE 9/i.test(navigator.userAgent) || /MSIE 10/i.test(navigator.userAgent) || /rv:11.0/i.test(navigator.userAgent) || /Edge\/\d./i.test(navigator.userAgent))) {
    return true;
  }
  return false;
};
var getRefCurrent = function (myRef) {
  return /* istanbul ignore next */(
    myRef && myRef.current ? myRef.current : null
  );
};

// for scrolling with transition
var SPEED = 25;
var DISTANCE = 120;
var STEP = 20;

// eslint-disable-next-line max-statements
var Tabs = function (_ref) {
  var _ref$name = _ref.name,
    name = _ref$name === void 0 ? 'TabsGroup' : _ref$name,
    selectedTabIdx = _ref.selectedTabIdx,
    center = _ref.center,
    _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
    onClick = _ref.onClick,
    className = _ref.className,
    tabClassName = _ref.tabClassName,
    tabsWrapperClassName = _ref.tabsWrapperClassName,
    children = _ref.children,
    _ref$scrollLeftLabel = _ref.scrollLeftLabel,
    scrollLeftLabel = _ref$scrollLeftLabel === void 0 ? 'scroll left' : _ref$scrollLeftLabel,
    _ref$scrollRightLabel = _ref.scrollRightLabel,
    scrollRightLabel = _ref$scrollRightLabel === void 0 ? 'scroll right' : _ref$scrollRightLabel,
    keepAlive = _ref.keepAlive,
    lazyLoad = _ref.lazyLoad,
    addBackgroundColor = _ref.addBackgroundColor;
  var navEleRef = useRef();
  var containerEleRef = useRef();
  var prevSelectedTabIdxRef = useRef(0);
  var sliderRef = useRef();
  var tabRef = useRef([]);
  var isTabsFocusedRef = useRef(false);
  var focusedIdxRef = useRef();
  var _useState = useState('none'),
    _useState2 = _slicedToArray(_useState, 2),
    overflowDir = _useState2[0],
    setOverflowDir = _useState2[1];
  var getSelectedTabIdx = function () {
    var index = typeof selectedTabIdx === 'string' ? parseInt(selectedTabIdx, 10) : Math.floor(selectedTabIdx);
    if (!selectedTabIdx || !index || index >= tabs.length || index < 0) {
      return 0;
    }
    return index;
  };

  /* istanbul ignore next */
  var determineOverflow = function () {
    var nav = getRefCurrent(navEleRef);
    if (nav) {
      var tabsWrapper = nav.children[0];
      var _scroll = nav.scrollLeft;
      var navRight = Math.floor(nav.getBoundingClientRect().right);
      var tabsRight = Math.floor(tabsWrapper.getBoundingClientRect().right);
      var navLeft = Math.floor(nav.getBoundingClientRect().left);
      var tabsLeft = Math.floor(tabsWrapper.getBoundingClientRect().left);
      if (_scroll === 0 && navRight > tabsRight && tabsLeft > navLeft) {
        setOverflowDir('none');
        return;
      }
      if (tabsLeft < navLeft && navRight < tabsRight) {
        setOverflowDir('both');
        return;
      }
      if (tabsRight > navRight) {
        setOverflowDir('right');
        return;
      }
      if (tabsLeft < navLeft) {
        setOverflowDir('left');
        return;
      }
    }

    // record that the function was called
    var evt = new Event('checkOverflow');
    window.dispatchEvent(evt);
  };
  var getWrapperCurrent = function (w) {
    return /* istanbul ignore next */(
      w && w.current ? w.current : null
    );
  };

  // apply styles to the slider element based on the
  // dimention of the tab
  var applySliderStyles = function (element) {
    var _element$offsetParent;
    var slider = getWrapperCurrent(sliderRef);
    slider.style.left = "".concat((element === null || element === void 0 ? void 0 : element.getBoundingClientRect().left) - (element === null || element === void 0 ? void 0 : (_element$offsetParent = element.offsetParent) === null || _element$offsetParent === void 0 ? void 0 : _element$offsetParent.getBoundingClientRect().left), "px");
    slider.style.width = "".concat(element === null || element === void 0 ? void 0 : element.getBoundingClientRect().width, "px");
  };
  var offsetFocus = function (offset) {
    var nav = getRefCurrent(navEleRef);
    var tabDomItems = nav.querySelectorAll('[role="tab"]');
    var nextIndex = focusedIdxRef.current + offset;
    var index;
    /* istanbul ignore next */
    if (nextIndex === tabs.length) {
      index = 0;
    } else if (nextIndex === -1) {
      index = tabs.length - 1;
    } else {
      index = nextIndex;
    }
    tabDomItems[index].focus();
    tabDomItems[index].scrollIntoView({
      block: 'nearest'
    });
    focusedIdxRef.current = index;
  };
  var keyHandler = function (event) {
    /* istanbul ignore next */
    var KeyCode = new KeyboardCode(event.key || event.keyCode);
    var selectedIndex = getSelectedTabIdx();
    if (KeyCode.IS_ARROW_LEFT) {
      event.preventDefault();
      offsetFocus(-1);
    } else if (KeyCode.IS_ARROW_RIGHT) {
      event.preventDefault();
      offsetFocus(1);
    } else if (KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) {
      event.preventDefault();
      /* istanbul ignore next */
      if (selectedIndex !== focusedIdxRef.current) {
        onClick(event, focusedIdxRef.current);
      }
    }
  };
  var handleFocusOut = function (event) {
    isTabsFocusedRef.current = false;
    var newFocusedItem = event.relatedTarget;
    /* istanbul ignore next */
    if (!newFocusedItem || newFocusedItem.parentElement !== event.currentTarget) {
      // @ts-ignore
      document.removeEventListener('keydown', keyHandler, false);
      // reset focusedIdx to current selected tab
      focusedIdxRef.current = getSelectedTabIdx();
      // add scroll arrows back when focus is out:
      determineOverflow();
    }
  };
  useLayoutEffect(function () {
    var nav = getRefCurrent(navEleRef);
    prevSelectedTabIdxRef.current = getSelectedTabIdx();

    /* istanbul ignore next */
    if (isTabsFocusedRef.current) {
      // @ts-ignore
      document.addEventListener('keydown', keyHandler, false);
    }
    nav.firstChild.addEventListener('focusout',
    // @ts-ignore
    handleFocusOut, false);
    return function () {
      // @ts-ignore
      document.removeEventListener('keydown', keyHandler, false);
      nav.firstChild.removeEventListener('focusout',
      // @ts-ignore
      handleFocusOut, false);
    };
  }, [tabs, selectedTabIdx, onClick]);
  useLayoutEffect(function () {
    var container = getRefCurrent(containerEleRef);
    var nav = getRefCurrent(navEleRef);
    var resizeObserver;
    focusedIdxRef.current = getSelectedTabIdx();
    var scrollButtons = container.querySelectorAll("[data-id=\"scroll-btn\"]");
    if (!isRunningOnIEorEdge()) {
      determineOverflow();
      // Check for overflow after window resize:
      /* istanbul ignore next */
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(function () {
          determineOverflow();
        });
        resizeObserver.observe(nav);
      }

      // adjust left/right arrow buttons height tof the tabs wrapper.
      var tabsWrapper = nav.children[0];
      /* istanbul ignore next */
      var btnHeight = (tabsWrapper.offsetHeight + tabsWrapper.offsetTop * 2).toString();
      /* istanbul ignore next */
      if (scrollButtons && tabsWrapper.offsetHeight !== scrollButtons[0].offsetHeight) {
        scrollButtons[0].style.height = "".concat(btnHeight, "px");
        scrollButtons[1].style.height = "".concat(btnHeight, "px");
      }
    }
    return function () {
      /* istanbul ignore next */
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);
  useLayoutEffect(function () {
    // For the first time during initialisation
    focusedIdxRef.current = getSelectedTabIdx();

    /* istanbul ignore next */
    var resizeObserver = window.ResizeObserver ? new ResizeObserver(throttle(function () {
      applySliderStyles(tabRef.current[focusedIdxRef.current]);
    }, 100)) : null;

    /* istanbul ignore next */
    if (window.ResizeObserver) {
      resizeObserver.observe(containerEleRef.current);
    }
    return function () {
      /* istanbul ignore next */
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [selectedTabIdx]);
  var handleClick = function (event) {
    var nav = getRefCurrent(navEleRef);
    var tabDomItems = nav.querySelectorAll('[role="tab"]');
    applySliderStyles(event.target);
    isTabsFocusedRef.current = false;
    /* istanbul ignore next */
    if (tabDomItems[focusedIdxRef.current]) {
      tabDomItems[focusedIdxRef.current].blur();
    }
    var tabIdxClicked = parseInt(event.currentTarget.getAttribute('data-tab-index'), 10);
    // This is tested, but coverage not registering it
    /* istanbul ignore next */
    if (tabIdxClicked) {
      focusedIdxRef.current = tabIdxClicked;
    }
    onClick(event, tabIdxClicked);
  };
  var handleFocus = function (event) {
    isTabsFocusedRef.current = true;
    var prevFocusedItem = event.relatedTarget;
    if (!prevFocusedItem || prevFocusedItem.parentElement !== event.currentTarget) {
      // @ts-ignore
      document.addEventListener('keydown', keyHandler, false);
      // remove arrows when navigating with keyboard:
      setOverflowDir('none');
    }
  };
  var handleTouchScroll = function () {
    var isScrolling;
    window.clearTimeout(isScrolling);
    /* istanbul ignore next */
    isScrolling = setTimeout(function () {
      determineOverflow();
    }, 15);
  };

  /* istanbul ignore next */
  var scroll = function (direction) {
    var nav = getRefCurrent(navEleRef);
    // scroll with transition
    var scrollAmount = 0;
    var slideTimer = window.setInterval(function () {
      if (direction === 'right') {
        nav.scrollLeft += STEP;
      } else {
        nav.scrollLeft -= STEP;
      }
      scrollAmount += STEP;
      if (scrollAmount >= DISTANCE) {
        window.clearInterval(slideTimer);
      }
    }, SPEED);
    setTimeout(function () {
      determineOverflow();
    }, 150);

    // record that scroll event was called
    var evt = new Event('tabsScrolled');
    window.dispatchEvent(evt);
  };
  var selectedIndex = getSelectedTabIdx();
  var leftScrollStyles = cx(styles.tabsScrollBtn, styles.tabsScrollBtnLeft, /* istanbul ignore next */
  (overflowDir === 'left' || overflowDir === 'both') && styles.tabsScrollBtnVisible, isRunningOnIEorEdge() && styles.hideBtn);
  var rightScrollStyles = cx(styles.tabsScrollBtn, styles.tabsScrollBtnRight, /* istanbul ignore next */
  (overflowDir === 'right' || overflowDir === 'both') && styles.tabsScrollBtnVisible, isRunningOnIEorEdge() && styles.hideBtn);
  var scrollButtonFocus = -1;
  var selectedId;
  var tabItems = tabs.map(function (tab, index) {
    var id = tab.id,
      panelId = tab.panelId,
      label = tab.label,
      otherTabProps = _objectWithoutProperties(tab, _excluded$1);
    // update animation (only on the selected tab)
    if (index === selectedIndex) {
      selectedId = tab.id || "".concat(name, "-tab").concat(index + 1);
    }
    return /*#__PURE__*/React.createElement(Tab, _extends({
      key: "".concat(name, "-tab").concat(index + 1),
      id: id,
      ref: function ref(el) {
        return tabRef.current[index] = el;
      },
      tabIdx: index.toString(),
      label: label,
      tabGroupId: name,
      panelId: panelId,
      selected: index === selectedIndex,
      onClick: handleClick,
      className: tabClassName
    }, otherTabProps, {
      "data-ppui": "true"
    }));
  });

  // Get selected tab panel and update selected tab panel props
  var tabContentItem;
  if (keepAlive || lazyLoad) {
    tabContentItem = React.Children.toArray(children).map(function (_panel, idx) {
      var isSelected = selectedIndex === idx;

      /* istanbul ignore next */
      return React.cloneElement(_panel, {
        hidden: !isSelected,
        tabId: isSelected ? selectedId : undefined,
        visible: keepAlive ? true : isSelected
      });
    });
  } else {
    var tabPanel = React.Children.toArray(children).filter(function (_panel, idx) {
      return selectedIndex === idx;
    })[0];
    tabContentItem = React.cloneElement(tabPanel, {
      tabId: selectedId,
      hidden: false
    });
  }
  var otherIconButtonAttr = {
    buttonType: 'secondary',
    className: styles.tabsArrowIcon
  };
  var navStyles = cx(styles.base, addBackgroundColor && styles.navBackground);
  var tabsWrapperStyles = cx(styles.tabsWrapper, center && styles.tabsCenter, tabsWrapperClassName);
  return /*#__PURE__*/React.createElement("div", {
    ref: containerEleRef,
    id: name,
    className: cx(styles.tabsWrapperBase, className),
    "data-ppui-info": "tabs_4.0.6__8.5.0"
  }, /*#__PURE__*/React.createElement("nav", {
    className: navStyles,
    id: "".concat(name, "-nav"),
    ref: navEleRef,
    onScroll: function onScroll() {
      return handleTouchScroll();
    },
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement("div", {
    id: "".concat(name, "-tabs"),
    className: tabsWrapperStyles,
    role: "tablist",
    onFocus: function onFocus(e) {
      return handleFocus(e);
    },
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    } // remove focus on click
    ,
    "data-ppui": "true"
  }, tabItems, /*#__PURE__*/React.createElement("div", {
    ref: sliderRef,
    className: styles.tabsSlider,
    "data-ppui": "true"
  }))), /*#__PURE__*/React.createElement("span", {
    "data-id": "scroll-btn",
    className: leftScrollStyles,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement(IconButton, _extends({
    inverse: true,
    svgIconComponent: ArrowLeftIcon,
    "aria-label": scrollLeftLabel,
    tabIndex: scrollButtonFocus,
    onClick: function () {
      return scroll('left');
    }
  }, otherIconButtonAttr, {
    "data-ppui": "true"
  }), scrollLeftLabel)), /*#__PURE__*/React.createElement("span", {
    "data-id": "scroll-btn",
    className: rightScrollStyles,
    "data-ppui": "true"
  }, /*#__PURE__*/React.createElement(IconButton, _extends({
    inverse: true,
    svgIconComponent: ArrowRightIcon,
    tabIndex: scrollButtonFocus,
    onClick: function () {
      return scroll('right');
    }
  }, otherIconButtonAttr, {
    "data-ppui": "true"
  }), scrollRightLabel)), tabContentItem);
};

var _excluded = ["id", "tabId", "hidden", "className", "visible", "children"];
var TabPanel = function (_ref) {
  var id = _ref.id,
    tabId = _ref.tabId,
    hidden = _ref.hidden,
    className = _ref.className,
    _ref$visible = _ref.visible,
    visible = _ref$visible === void 0 ? true : _ref$visible,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var hasMouseClickFocus = useRef(false);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    mouseFocus = _useState2[0],
    updateMouseFocus = _useState2[1];

  // Ref<boolean> variable to determine whether to render the component or not.
  var hasRendered = useRef(visible);

  /* istanbul ignore next */
  if (visible && !hasRendered.current) {
    hasRendered.current = true;
  }

  /* istanbul ignore next */
  if (!hasRendered.current) {
    // Do not re-render the TabPanel when its already present in the DOM.
    return null;
  }

  // to prevent tab panel from getting focus on mouse-click

  var tabContentStyles = cx(styles.tabsContent, !mouseFocus && styles.tabsContentFocus, /* istanbul ignore next */
  visible ? styles.tabsContentVisible : styles.tabsContentHidden, className);
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: tabContentStyles,
    "aria-labelledby": tabId,
    "aria-hidden": hidden,
    tabIndex: 0,
    role: "tabpanel",
    onFocus: function handleFocus() {
      var hasClickFocus = hasMouseClickFocus.current ? true : false;
      updateMouseFocus(hasClickFocus);
      hasMouseClickFocus.current = false;
    },
    onMouseDown: function handleMouseDown() {
      hasMouseClickFocus.current = true;
    }
  }, rest, {
    "data-ppui": "true"
  }), children);
};

export { Tab, TabPanel, Tabs };
